import { MenuProps } from 'antd';
import {
  BulkMessagingCampaignRecipientStatusEnum,
  BulkMessagingCampaignStatusEnum,
  BulkMessagingRunStatusEnum,
} from '@/types/generated/graphql';

export const BULK_MESSAGING_BASE_PATH = '/tools/bulk-messaging';

export type BulkMessagingMode = 'regular' | 'a2pMessaging';

// Twilio SMS message body character limit for bulk campaigns.
export const MAX_BULK_SMS_MESSAGE_LENGTH = 1600;

// Chip colors for the BulkMessagingCampaignStatusEnum values returned by the campaign APIs.
// Keyed as a Record against the enum so a new status added to the enum fails to compile here until styled.
export const BULK_MESSAGING_CAMPAIGN_STATUS_STYLES: Record<BulkMessagingCampaignStatusEnum, string> = {
  [BulkMessagingCampaignStatusEnum.active]: 'bg-primary-50! text-primary-500! border-primary-100!',
  [BulkMessagingCampaignStatusEnum.draft]: 'bg-surface! text-text-700! border-gray-500!',
  [BulkMessagingCampaignStatusEnum.scheduled]: 'bg-primary-50! text-primary-500! border-primary-100!',
  [BulkMessagingCampaignStatusEnum.pending]: 'bg-primary-50! text-primary-500! border-primary-100!',
  [BulkMessagingCampaignStatusEnum.paused]:
    'bg-orange-50! text-orange-700! border-orange-200! dark:bg-orange-500/15! dark:text-orange-400! dark:border-orange-500/30!',
  [BulkMessagingCampaignStatusEnum.completed]: 'bg-emerald-50! text-emerald-700! border-emerald-200!',
  [BulkMessagingCampaignStatusEnum.cancelled]: 'bg-red-50! text-red-700! border-red-200!',
  [BulkMessagingCampaignStatusEnum.failed]: 'bg-red-50! text-red-700! border-red-200!',
  [BulkMessagingCampaignStatusEnum.partial_failed]: 'bg-red-50! text-red-700! border-red-200!',
};

// Labels for the BulkMessagingCampaignStatusEnum values returned by the campaign APIs.
export const BULK_MESSAGING_CAMPAIGN_STATUS_LABELS: Record<BulkMessagingCampaignStatusEnum, string> = {
  [BulkMessagingCampaignStatusEnum.active]: 'Active',
  [BulkMessagingCampaignStatusEnum.cancelled]: 'Cancelled',
  [BulkMessagingCampaignStatusEnum.completed]: 'Completed',
  [BulkMessagingCampaignStatusEnum.draft]: 'Draft',
  [BulkMessagingCampaignStatusEnum.failed]: 'Failed',
  [BulkMessagingCampaignStatusEnum.partial_failed]: 'Partial Failed',
  [BulkMessagingCampaignStatusEnum.paused]: 'Paused',
  [BulkMessagingCampaignStatusEnum.pending]: 'Pending',
  [BulkMessagingCampaignStatusEnum.scheduled]: 'Scheduled',
};

export const BULK_MESSAGING_STATUS_OPTIONS = [
  { key: 'draft', label: 'Draft', value: 'draft' },
  { key: 'pending', label: 'Pending', value: 'pending' },
  { key: 'scheduled', label: 'Scheduled', value: 'scheduled' },
  { key: 'active', label: 'Active', value: 'active' },
  { key: 'paused', label: 'Paused', value: 'paused' },
  { key: 'completed', label: 'Completed', value: 'completed' },
  { key: 'cancelled', label: 'Cancelled', value: 'cancelled' },
  { key: 'failed', label: 'Failed', value: 'failed' },
  { key: 'partial_failed', label: 'Partial Failed', value: 'partial_failed' },
];

export const COMPOSE_MESSAGE_TYPE_OPTIONS = [
  { key: 'regular', label: 'Regular' },
  { key: 'a2pMessaging', label: 'A2P Messaging' },
];

export const RECURRING_MESSAGE_REPEAT_OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
];

export const BULK_MESSAGING_TABS = [
  { key: 'all', label: 'All' },
  { key: 'one_time', label: 'One-Time' },
  { key: 'recurring', label: 'Recurring' },
];

// Labels for the BulkMessagingCampaignRecipientStatusEnum values returned by the recipients API.
export const BULK_MESSAGE_REPORT_RECIPIENT_STATUS_LABELS: Record<BulkMessagingCampaignRecipientStatusEnum, string> = {
  [BulkMessagingCampaignRecipientStatusEnum.delivered]: 'Delivered',
  [BulkMessagingCampaignRecipientStatusEnum.failed]: 'Failed',
  [BulkMessagingCampaignRecipientStatusEnum.pending]: 'Pending',
  [BulkMessagingCampaignRecipientStatusEnum.processing]: 'Processing',
  [BulkMessagingCampaignRecipientStatusEnum.sent]: 'Sent',
  [BulkMessagingCampaignRecipientStatusEnum.undelivered]: 'Undelivered',
};

export const BULK_MESSAGE_REPORT_STATUS_ITEMS: MenuProps['items'] = Object.values(
  BulkMessagingCampaignRecipientStatusEnum
).map((value) => ({ key: value, label: BULK_MESSAGE_REPORT_RECIPIENT_STATUS_LABELS[value] }));

export const BULK_MESSAGE_REPORT_REASON_ITEMS: MenuProps['items'] = [
  { key: 'A2P non Compliance', label: 'A2P non Compliance' },
  { key: 'Invalid Number', label: 'Invalid Number' },
  { key: 'No Reason', label: 'No Reason' },
];

export const BULK_MESSAGE_REPORT_RUN_CONTROL_ITEMS: MenuProps['items'] = [
  { key: 'Complete', label: 'Complete' },
  { key: 'Incomplete', label: 'Incomplete' },
  { key: 'In Progress', label: 'In Progress' },
];

// Labels for the BulkMessagingRunStatusEnum values returned by the campaign-runs API.
export const BULK_MESSAGING_RUN_STATUS_LABELS: Record<BulkMessagingRunStatusEnum, string> = {
  [BulkMessagingRunStatusEnum.pending]: 'Pending',
  [BulkMessagingRunStatusEnum.running]: 'Running',
  [BulkMessagingRunStatusEnum.completed]: 'Completed',
  [BulkMessagingRunStatusEnum.partial_failed]: 'Partial Failed',
  [BulkMessagingRunStatusEnum.failed]: 'Failed',
  [BulkMessagingRunStatusEnum.force_stopped]: 'Force Stopped',
  [BulkMessagingRunStatusEnum.cancelled]: 'Cancelled',
};

// Chip colors for the BulkMessagingRunStatusEnum values (used for run/delivery status chips).
export const BULK_MESSAGING_RUN_STATUS_STYLES: Record<BulkMessagingRunStatusEnum, string> = {
  [BulkMessagingRunStatusEnum.pending]: 'bg-primary-50! text-primary-500! border-primary-100!',
  [BulkMessagingRunStatusEnum.running]: 'bg-primary-50! text-primary-500! border-primary-100!',
  [BulkMessagingRunStatusEnum.completed]: 'bg-emerald-50! text-emerald-700! border-emerald-200!',
  [BulkMessagingRunStatusEnum.partial_failed]:
    'bg-orange-50! text-orange-700! border-orange-200! dark:bg-orange-500/15! dark:text-orange-400! dark:border-orange-500/30!',
  [BulkMessagingRunStatusEnum.failed]: 'bg-red-50! text-red-700! border-red-200!',
  [BulkMessagingRunStatusEnum.force_stopped]: 'bg-red-50! text-red-700! border-red-200!',
  [BulkMessagingRunStatusEnum.cancelled]: 'bg-red-50! text-red-700! border-red-200!',
};

// Status / Run Control filter options for recurring campaign runs (all enums the API accepts).
export const BULK_MESSAGING_RUN_STATUS_ITEMS: MenuProps['items'] = Object.values(BulkMessagingRunStatusEnum).map(
  (value) => ({ key: value, label: BULK_MESSAGING_RUN_STATUS_LABELS[value] })
);
